// Adressen ohne Koordinaten nachträglich auflösen.
//
// Abhol- und Zieladresse bekommen ihre Koordinaten normalerweise über den
// Klick auf einen Autocomplete-Vorschlag (`onPlaceSelect`). Tippt der Disponent
// die Adresse nur ein — oder übernimmt sie aus einem Anruf-Entwurf, einer
// Vorlage ohne Koordinaten — bleibt der Text ohne Position. Die Fahrt landete
// dann mit (0,0) in Firestore: keine Route und keine Marker auf der LiveMap,
// unbrauchbare Entfernungs-/Preisschätzung und wertlose Zonenprüfung.
//
// Bewusst der Maps-JS-Geocoder und nicht der Geocoding-Webservice: Der
// Web-Maps-Schlüssel des Portals ist referrer-beschränkt, und der Webservice
// lehnt solche Schlüssel grundsätzlich ab (REQUEST_DENIED). Die JS-Bibliothek
// geht einen anderen Weg (siehe docs/general/maps_key_migration_konzept.md §3.2
// im App-Repo).

// Der Geocoder steht erst zur Verfügung, wenn die Maps-JS-Bibliothek geladen
// ist. Das Formular kann früher abgeschickt werden, deshalb kurz warten statt
// sofort aufzugeben.
const waitForGeocoder = async () => {
  for (let attempt = 0; attempt < 30; attempt += 1) {
    if (window.google?.maps?.Geocoder) return window.google.maps.Geocoder;
    if (window.google?.maps?.importLibrary) {
      try {
        await window.google.maps.importLibrary('geocoding');
        if (window.google?.maps?.Geocoder) return window.google.maps.Geocoder;
      } catch (error) {
        // Nächster Versuch nach der Wartezeit unten.
      }
    }
    await new Promise((resolve) => setTimeout(resolve, 150));
  }
  return null;
};

/**
 * Sind in einem Location-Objekt der Formulare brauchbare Koordinaten hinterlegt?
 * (0,0) gilt wie in der LiveMap als "keine Koordinate", nicht als Punkt im
 * Golf von Guinea.
 *
 * @param {{latitude: number, longitude: number}|null} location
 */
export const hasCoordinates = (location) => {
  const lat = location?.latitude;
  const lng = location?.longitude;
  return (
    typeof lat === 'number' && Number.isFinite(lat) &&
    typeof lng === 'number' && Number.isFinite(lng) &&
    !(lat === 0 && lng === 0)
  );
};

/**
 * Löst einen freien Adresstext in Koordinaten auf.
 *
 * @param {string} address        Adresse, wie sie im Feld steht.
 * @param {object} [options]
 * @param {object} [options.bounds] LatLngBoundsLiteral des Betriebsgebiets;
 *                                  bevorzugt lokale Treffer (weicher Bias).
 * @returns {Promise<{latitude:number, longitude:number, formattedAddress:string}|null>}
 *          `null`, wenn die Adresse nicht auflösbar ist oder Maps fehlt.
 */
export const geocodeAddressToLocation = async (address, options = {}) => {
  const query = (address || '').trim();
  if (!query) return null;

  const Geocoder = await waitForGeocoder();
  if (!Geocoder) return null;

  const request = { address: query, componentRestrictions: { country: 'DE' } };
  if (options.bounds) request.bounds = options.bounds;

  try {
    const result = await new Promise((resolve) => {
      new Geocoder().geocode(request, (results, status) => {
        resolve(status === 'OK' ? results?.[0] || null : null);
      });
    });

    const location = result?.geometry?.location;
    if (!location) return null;

    const latitude = location.lat();
    const longitude = location.lng();
    if (!hasCoordinates({ latitude, longitude })) return null;

    return { latitude, longitude, formattedAddress: result.formatted_address || query };
  } catch (error) {
    console.warn('Geocoding fehlgeschlagen:', error?.message);
    return null;
  }
};

const ADDRESS_RESULT_TYPES = ['street_address', 'premise', 'subpremise'];

/**
 * Ist ein Geocoding-Treffer genau genug, um getippten Text ohne Vorschlag zu
 * übernehmen (Vorschlagspflicht, nextcab_mobile/docs/general/adressauswahl_konzept.md
 * §3)? Nur echte Hausadressen: Adress-Treffer (kein benannter Ort), Hausnummer
 * vorhanden, kein Teiltreffer, Punkt am Gebäude oder auf der
 * Hausnummern-Interpolation.
 *
 * Benannte Orte fallen bewusst durch, auch mit Hausnummer: Mit dem Bias aufs
 * Betriebsgebiet liefert der Geocoder für „Flughafen Hamburg" den POI
 * „Hamburg Arpt (HAM), Flughafenstr. 1-3" (ROOFTOP, mit Hausnummer) — genau
 * den Punkt auf dem Gelände. Solche Orte gehen nur per Vorschlag (mit
 * Terminal-Auswahl) oder Kartenpunkt.
 *
 * @param {object|null} result  google.maps.GeocoderResult
 */
export const isPreciseGeocodeResult = (result) => {
  if (!result || result.partial_match) return false;
  const types = result.types || [];
  if (!types.some((type) => ADDRESS_RESULT_TYPES.includes(type))) return false;
  if (types.includes('establishment') || types.includes('point_of_interest')) return false;
  const hasStreetNumber = (result.address_components || [])
    .some((component) => (component.types || []).includes('street_number'));
  const locationType = result.geometry?.location_type;
  return hasStreetNumber && (locationType === 'ROOFTOP' || locationType === 'RANGE_INTERPOLATED');
};

/**
 * Wie geocodeAddressToLocation, aber nur für präzise Hausadressen
 * (isPreciseGeocodeResult) — sonst null.
 */
export const geocodePreciseAddress = async (address, options = {}) => {
  const query = (address || '').trim();
  if (!query) return null;

  const Geocoder = await waitForGeocoder();
  if (!Geocoder) return null;

  const request = { address: query, componentRestrictions: { country: 'DE' } };
  if (options.bounds) request.bounds = options.bounds;

  try {
    const result = await new Promise((resolve) => {
      new Geocoder().geocode(request, (results, status) => {
        resolve(status === 'OK' ? results?.[0] || null : null);
      });
    });
    if (!isPreciseGeocodeResult(result)) return null;

    const latitude = result.geometry.location.lat();
    const longitude = result.geometry.location.lng();
    if (!hasCoordinates({ latitude, longitude })) return null;

    return { latitude, longitude, formattedAddress: result.formatted_address || query };
  } catch (error) {
    console.warn('Geocoding fehlgeschlagen:', error?.message);
    return null;
  }
};

export default geocodeAddressToLocation;
